import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { pbEngineer } from '../lib/pb';
import { useData } from '../data/DataProvider';
import EmptyState from '../shared/components/EmptyState';
import Skeleton from '../shared/components/Skeleton';
import { Icons } from '../shared/icons';
import SafeIcon from '../common/SafeIcon';

export default function EngineerJobSheets() {
  const { data } = useData();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobSheets, setJobSheets] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadJobSheets();
  }, [filter]);

  const loadJobSheets = async () => {
    try {
      const engineerId = pbEngineer.authStore.record?.id;
      if (!engineerId) return;

      let filterQuery = `engineer_job_id.engineer_id="${engineerId}"`;
      if (filter !== 'all') {
        filterQuery += ` && status="${filter}"`;
      }

      const result = await pbEngineer.collection('engineer_job_sheets').getList(1, 50, {
        filter: filterQuery,
        sort: '-created',
        expand: 'engineer_job_id,client_id',
      });

      setJobSheets(result.items);
    } catch (err) {
      console.error('Failed to load job sheets:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'gray',
      in_progress: 'blue',
      completed: 'green',
      submitted: 'purple'
    };
    return colors[status] || 'gray';
  };

  const handleCreateNew = () => {
    navigate('new');
  };

  if (loading) {
    return <div className="page-container"><Skeleton rows={6} /></div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Digital Job Sheets</h1>
        <button className="primary-button" onClick={handleCreateNew}>
          <SafeIcon icon={Icons.Plus} />
          New Job Sheet
        </button>
      </div>

      <div className="filter-bar">
        {['all', 'draft', 'in_progress', 'completed', 'submitted'].map(status => (
          <button
            key={status}
            className={`filter-button ${filter === status ? 'active' : ''}`}
            onClick={() => setFilter(status)}
          >
            {status.replace('_', ' ').toUpperCase()}
          </button>
        ))}
      </div>

      {jobSheets.length === 0 ? (
        <EmptyState
          title="No job sheets found"
          message={filter === 'all' 
            ? "You haven't created any job sheets yet" 
            : `No ${filter.replace('_', ' ')} job sheets found`}
          action="Create First Job Sheet"
          onAction={handleCreateNew}
          icon={Icons.FileText}
        />
      ) : (
        <div className="job-sheets-grid">
          {jobSheets.map(sheet => (
            <div key={sheet.id} className="job-sheet-card">
              <div className="sheet-header">
                <div className={`sheet-status status-${getStatusColor(sheet.status)}`}>
                  {sheet.status.replace('_', ' ')}
                </div>
                <span className="sheet-date">
                  {new Date(sheet.created).toLocaleDateString()}
                </span>
              </div>
              
              <h3>{sheet.expand?.engineer_job_id?.title || 'Job Sheet'}</h3>
              
              <div className="sheet-client">
                <SafeIcon icon={Icons.User} />
                {sheet.expand?.client_id?.name || 'Unknown Client'}
              </div>

              {sheet.work_summary && (
                <p className="sheet-summary">{sheet.work_summary}</p>
              )}

              {sheet.materials_used && (
                <div className="sheet-materials">
                  <SafeIcon icon={Icons.Package} />
                  <span>Materials logged</span>
                </div>
              )}

              {sheet.photos && Array.isArray(sheet.photos) && sheet.photos.length > 0 && (
                <div className="sheet-photos">
                  <SafeIcon icon={Icons.Camera} />
                  <span>{sheet.photos.length} photos</span>
                </div>
              )}

              <button className="secondary-button" onClick={() => navigate(`${sheet.id}`)}>
                View Details
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}